import { useState, useEffect } from 'react';
import axios from 'axios';
import { RefreshCw, CheckCircle, XCircle, Clock, DollarSign, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const AdminWithdrawals = ({ token }) => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [actionType, setActionType] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [txRef, setTxRef] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/withdrawals/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWithdrawals(res.data);
    } catch { toast.error('Failed to load withdrawals'); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleAction = async () => {
    if (!selected) return;
    try {
      await axios.put(`${API}/withdrawals/admin/${selected.id}`, {
        action: actionType,
        admin_note: adminNote,
        transaction_ref: txRef || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Withdrawal ${actionType}d`);
      setShowDialog(false);
      setAdminNote('');
      setTxRef('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    }
  };

  const openAction = (withdrawal, action) => {
    setSelected(withdrawal);
    setActionType(action);
    setShowDialog(true);
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { color: 'bg-yellow-500', label: 'Pending' },
      approved: { color: 'bg-blue-500', label: 'Approved' },
      completed: { color: 'bg-green-500', label: 'Completed' },
      rejected: { color: 'bg-red-500', label: 'Rejected' },
    };
    const c = config[status] || { color: 'bg-gray-500', label: status };
    return <Badge className={`${c.color} text-white`}>{c.label}</Badge>;
  };

  const stats = {
    total: withdrawals.length,
    pending: withdrawals.filter(w => w.status === 'pending').length,
    approved: withdrawals.filter(w => w.status === 'approved').length,
    completed: withdrawals.filter(w => w.status === 'completed').length,
    totalAmount: withdrawals.filter(w => w.status === 'completed').reduce((s, w) => s + w.amount, 0),
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold">Withdrawal Requests</h2>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold">{stats.total}</p><p className="text-xs text-gray-500">Total</p></CardContent></Card>
        <Card className="bg-yellow-50"><CardContent className="p-3 text-center"><p className="text-xl font-bold text-yellow-600">{stats.pending}</p><p className="text-xs text-yellow-600">Pending</p></CardContent></Card>
        <Card className="bg-blue-50"><CardContent className="p-3 text-center"><p className="text-xl font-bold text-blue-600">{stats.approved}</p><p className="text-xs text-blue-600">Approved</p></CardContent></Card>
        <Card className="bg-green-50"><CardContent className="p-3 text-center"><p className="text-xl font-bold text-green-600">{stats.completed}</p><p className="text-xs text-green-600">Completed</p></CardContent></Card>
        <Card className="bg-amber-50"><CardContent className="p-3 text-center"><p className="text-xl font-bold text-amber-600">{stats.totalAmount.toFixed(1)}</p><p className="text-xs text-amber-600">Paid (kWh)</p></CardContent></Card>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Artist</TableHead>
                <TableHead>Amount (kWh)</TableHead>
                <TableHead>Bank</TableHead>
                <TableHead>Account</TableHead>
                <TableHead>Currency</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {withdrawals.map(w => (
                <TableRow key={w.id}>
                  <TableCell>
                    <p className="font-medium">{w.artist_name}</p>
                    <p className="text-xs text-gray-400">{w.artist_email}</p>
                  </TableCell>
                  <TableCell className="font-bold text-amber-600">{w.amount} kWh</TableCell>
                  <TableCell>{w.bank_name}</TableCell>
                  <TableCell className="text-xs font-mono">{w.account_number?.slice(0, 12)}...</TableCell>
                  <TableCell>{w.currency}</TableCell>
                  <TableCell>{getStatusBadge(w.status)}</TableCell>
                  <TableCell className="text-xs text-gray-500">{new Date(w.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {w.status === 'pending' && (
                        <>
                          <Button size="sm" className="bg-green-500 hover:bg-green-600" onClick={() => openAction(w, 'approve')}>Approve</Button>
                          <Button size="sm" variant="destructive" onClick={() => openAction(w, 'reject')}>Reject</Button>
                        </>
                      )}
                      {w.status === 'approved' && (
                        <Button size="sm" className="bg-blue-500 hover:bg-blue-600" onClick={() => openAction(w, 'complete')}>
                          <CheckCircle className="w-3 h-3 mr-1" /> Complete
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {withdrawals.length === 0 && <div className="text-center py-12 text-gray-500">No withdrawal requests</div>}
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === 'approve' ? 'Approve Withdrawal' : actionType === 'reject' ? 'Reject Withdrawal' : 'Complete Withdrawal'}
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p><strong>{selected.artist_name}</strong> — {selected.amount} kWh</p>
                <p className="text-sm text-gray-500">{selected.bank_name} · {selected.account_number}</p>
                {selected.swift_code && <p className="text-sm text-gray-500">SWIFT: {selected.swift_code}</p>}
              </div>
              {actionType === 'complete' && (
                <div>
                  <Label>Bank Transfer Reference</Label>
                  <Input value={txRef} onChange={e => setTxRef(e.target.value)} placeholder="Enter bank transfer reference number" />
                </div>
              )}
              <div>
                <Label>Admin Note</Label>
                <Textarea value={adminNote} onChange={e => setAdminNote(e.target.value)} placeholder="Optional note" className="h-20" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleAction} className={actionType === 'reject' ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'}>
              {actionType === 'approve' ? 'Approve' : actionType === 'reject' ? 'Reject' : 'Mark Completed'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminWithdrawals;
